// apps/web/src/pages/super/SuperRmsProxyPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGetJson, apiPostJson } from "../../utils/apiClient";

type SessionInfo = {
    sid: string;
    tenantId?: string;
    companyId?: string;
    shopUrl?: string;

    status?: string;
    createdBy?: string;

    createdAt?: string;
    lastSeenAt?: string;
    expiresAt?: string;
    endedAt?: string;
    endedBy?: string;
    error?: string;
};

function getQuery(name: string) {
    const sp = new URLSearchParams(window.location.search);
    return String(sp.get(name) ?? "").trim();
}

function normalizeViewerOrigin(raw?: string) {
    if (!raw) return "";
    let s = String(raw).trim();
    if (!s) return "";

    s = s.replace(/\/+$/, "");
    s = s.replace(/\/novnc(\?.*)?$/i, "");
    s = s.replace(/\/_novnc\/.*$/i, "");
    s = s.replace(/\/websockify(\?.*)?$/i, "");
    s = s.replace(/\?.*$/, "");

    return s;
}

export default function SuperRmsProxyPage() {
    const sid = useMemo(() => getQuery("sid"), []);
    const tenantIdHint = useMemo(() => getQuery("tenantId"), []);

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [info, setInfo] = useState<SessionInfo | null>(null);

    // ✅ ended 判定（status または endedAt で見る）
    const isEnded = useMemo(() => {
        const st = String(info?.status || "").toLowerCase();
        return st === "ended" || !!info?.endedAt;
    }, [info?.status, info?.endedAt]);

    const fetchInfo = useCallback(async (opts?: { silent?: boolean }) => {
        if (!sid) return;

        const silent = opts?.silent === true;
        if (!silent) {
            setErr("");
            setLoading(true);
        }

        try {
            const q = tenantIdHint ? `?tenantId=${encodeURIComponent(tenantIdHint)}` : "";
            const r = await apiGetJson(`/api/super/rms-proxy/${encodeURIComponent(sid)}${q}`);
            setInfo(r as SessionInfo);
        } catch (e: any) {
            if (!silent) setErr(e?.message ?? "セッション取得に失敗しました");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [sid, tenantIdHint]);

    // 初回ロード
    useEffect(() => {
        fetchInfo({ silent: false });
    }, [fetchInfo]);

    // ポーリング（状態表示用）
    useEffect(() => {
        if (!sid) return;
        const t = window.setInterval(() => {
            // ✅ 表示更新だけなので silent
            fetchInfo({ silent: true });
        }, 5000);
        return () => window.clearInterval(t);
    }, [sid, fetchInfo]);

    // heartbeat（viewer開いてる間だけ）※ ended なら止める
    useEffect(() => {
        if (!sid) return;
        if (isEnded) return; // ✅ ended なら送らない

        const t = window.setInterval(async () => {
            try {
                const q = tenantIdHint ? `?tenantId=${encodeURIComponent(tenantIdHint)}` : "";
                await apiPostJson(`/api/super/rms-proxy/${encodeURIComponent(sid)}/heartbeat${q}`, {});
            } catch {
                // 失敗しても黙る
            }
        }, 15000);
        return () => window.clearInterval(t);
    }, [sid, tenantIdHint, isEnded]);

    const endSession = useCallback(async () => {
        if (!sid) return;

        setErr("");
        setLoading(true);
        try {
            const q = tenantIdHint ? `?tenantId=${encodeURIComponent(tenantIdHint)}` : "";
            await apiPostJson(`/api/super/rms-proxy/${encodeURIComponent(sid)}/end${q}`, {});
            await fetchInfo({ silent: true });
        } catch (e: any) {
            setErr(e?.message ?? "終了に失敗しました");
        } finally {
            setLoading(false);
        }
    }, [sid, tenantIdHint, fetchInfo]);

    const viewerEnv =
        (process.env?.REACT_APP_RMS_PROXY_VIEWER_URL as string | undefined) || "";
    const viewerOrigin = useMemo(() => normalizeViewerOrigin(viewerEnv), [viewerEnv]);

    const viewerUrl = useMemo(() => {
        if (!viewerOrigin || !sid) return "";
        const q = new URLSearchParams();
        q.set("sid", sid);
        if (tenantIdHint) q.set("tenantId", tenantIdHint);
        return `${viewerOrigin}/novnc?${q.toString()}`;
    }, [viewerOrigin, sid, tenantIdHint]);

    const showViewer = !!viewerUrl && !isEnded; // ✅ ended なら iframe を隠す

    if (!sid) {
        return (
            <div className="w-full max-w-none min-w-0 h-full p-4">
                <h1 className="text-lg font-bold">RMS代理ログイン</h1>
                <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                    sid が指定されていません（/super/rms-proxy?sid=...）
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-none min-w-0 h-full flex flex-col gap-3 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-lg font-bold">RMS代理ログイン</h1>
                    <div className="text-xs text-slate-500 mt-1">
                        sid: <span className="font-mono">{sid}</span>
                        {tenantIdHint ? (
                            <>
                                {" "}
                                / tenantId: <span className="font-mono">{tenantIdHint}</span>
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className="border px-3 py-1 rounded text-xs bg-white hover:bg-gray-50"
                        onClick={() => fetchInfo({ silent: false })}
                        disabled={loading}
                    >
                        {loading ? "更新中..." : "更新"}
                    </button>

                    <button
                        className="border px-3 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        onClick={endSession}
                        disabled={loading || isEnded}
                        title="セッションを ended にします（ブラウザKillは別サービス側）"
                    >
                        終了
                    </button>

                    {viewerUrl ? (
                        <a
                            className="border px-3 py-1 rounded text-xs bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                            href={viewerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Viewerを別タブで開く（入口は /novnc）"
                        >
                            Viewerを開く
                        </a>
                    ) : null}
                </div>
            </div>

            {err ? (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3">
                    {err}
                </div>
            ) : null}

            {isEnded ? (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                    セッションは終了しました（ended）。Viewer表示/heartbeatは停止しています。
                </div>
            ) : null}

            <div className="border rounded bg-white p-3 text-sm">
                <div className="grid grid-cols-[10rem_1fr] gap-y-2 gap-x-4 text-xs">
                    <div className="text-slate-500">status</div>
                    <div className="font-semibold">{info?.status || "-"}</div>

                    <div className="text-slate-500">shopUrl</div>
                    <div className="font-mono">{info?.shopUrl || "-"}</div>

                    <div className="text-slate-500">companyId</div>
                    <div className="font-mono">{info?.companyId || "-"}</div>

                    <div className="text-slate-500">tenantId</div>
                    <div className="font-mono">{info?.tenantId || tenantIdHint || "-"}</div>

                    <div className="text-slate-500">createdAt</div>
                    <div className="font-mono">{info?.createdAt || "-"}</div>

                    <div className="text-slate-500">expiresAt</div>
                    <div className="font-mono">{info?.expiresAt || "-"}</div>

                    <div className="text-slate-500">lastSeenAt</div>
                    <div className="font-mono">{info?.lastSeenAt || "-"}</div>

                    <div className="text-slate-500">endedAt</div>
                    <div className="font-mono">{info?.endedAt || "-"}</div>

                    <div className="text-slate-500">endedBy</div>
                    <div className="font-mono">{info?.endedBy || "-"}</div>

                    <div className="text-slate-500">error</div>
                    <div className="font-mono text-red-700">{info?.error || "-"}</div>
                </div>
            </div>

            {showViewer ? (
                <div className="flex-1 min-h-0 border rounded overflow-hidden bg-white">
                    <iframe title="rms-proxy-viewer" src={viewerUrl} className="w-full h-full" />
                </div>
            ) : viewerUrl ? (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
                    Viewer は停止中です（ended）。必要なら新しいセッションを開始してください。
                </div>
            ) : (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
                    Viewer（noVNC等）が未設定です。<br />
                    REACT_APP_RMS_PROXY_VIEWER_URL を設定すると、このページに iframe 表示/別タブ起動できます。
                </div>
            )}
        </div>
    );
}